import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  NumberFormatter,
  Progress,
  Skeleton,
  Table
} from '@mantine/core';
import AppStack from '../../common/AppStack';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import PaymentPriceItem from '../../common/PaymentPriceItem';
import { triggerNotification } from '../../../helpers/notificationHelper';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import { Context as RegistrationAdminDashboardContext } from '../../../providers/RegistrationAdminDashboardProvider';
import RegCartOrderTransactionPaymentTable from './RegCartOrderTransactionPaymentTable';
import { translateRegCartOrderData } from '../../../helpers/registrationHelper';
import RecordPaymentPriceItem from '../financials/RecordPaymentPriceItem';

const RegCartOrderTransactionModal = ({
  pkRegCart,
  regCartOrder,
  isOpen,
  onClose,
  translatedOrder,
  onShowRefund
}) => {
  const { state: dashboardState } = useContext(
    RegistrationAdminDashboardContext
  );
  const { state, fetchRegCartOrder } = useContext(RegistrationAdminContext);

  const cartOrder =
    regCartOrder ||
    (pkRegCart?.toString() === state.regCart.value?.pkRegCart.toString()
      ? state.regCart.value
      : null);
  const loading = !cartOrder;
  const checkoutBalance = cartOrder?.cart;
  const currency = checkoutBalance?.currency?.toUpperCase() ?? 'CAD';

  const {
    regFormSubmissions: regFormSubmissionData,
    ecomOrder: translatedEcomOrder,
    invoices,
    invoiceTotals
  } = translatedOrder || translateRegCartOrderData(cartOrder);
  const { ecomOrderProducts: ecomOrderProductData } = translatedEcomOrder;

  const totalAmountPaid =
    checkoutBalance.amountPaidInCents + invoiceTotals.amountPaidInCents;
  const totalAmountRefunded =
    checkoutBalance.refundedAmountInCents + invoiceTotals.refundedAmountInCents;
  const totalTransactionAmount =
    checkoutBalance.totalInCents + invoiceTotals.totalInCents;

  const statusInfo = checkoutBalance
    ? totalAmountPaid >= 0 && totalAmountRefunded >= totalAmountPaid
      ? { label: 'Refunded', color: '#C40000' }
      : totalAmountRefunded > 0
      ? { label: 'Partially Refunded', color: '#666' }
      : totalAmountPaid >= totalTransactionAmount
      ? { label: 'Paid in full (payments)', color: 'dodgerblue' }
      : { label: 'Waiting for payment', color: '#000' }
    : {};

  const { value: regAssociation } = dashboardState.regAssociation;

  useEffect(() => {
    if (isOpen && pkRegCart && !cartOrder) {
      fetchRegCartOrder(
        regAssociation.pkEcomStore,
        { pkRegCart },
        null,
        (e) => {
          triggerNotification(e);
        }
      );
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={{
        cancelTitle: 'Close',
        onCancel: onClose,
        isSubmitHidden: true
      }}
      isOpen={isOpen}
      onClose={onClose}
      size={1100}
      title="Transaction Details"
    >
      <AppStack style={{ gap: 40 }}>
        <AppFlexbox
          style={{
            gap: 40,
            alignItems: 'start'
          }}
        >
          <AppStack style={{ flex: 1, gap: 5, alignSelf: 'stretch' }}>
            <AppText
              style={{ color: 'dodgerblue', fontSize: 18, fontWeight: 500 }}
            >
              Order status
            </AppText>
            <Divider size={2} />
            {loading ? (
              <AppStack style={{ gap: 5 }}>
                <Skeleton height={12} width="40%" />
                <Skeleton height={8} width="100%" />
              </AppStack>
            ) : (
              <AppStack style={{ gap: 0 }}>
                <AppText
                  style={{ fontSize: 16, color: statusInfo.color }}
                  weight={500}
                >
                  {statusInfo.label}
                </AppText>
                <Progress
                  value={
                    ((totalAmountPaid - totalAmountRefunded) /
                      totalTransactionAmount) *
                    100
                  }
                />
              </AppStack>
            )}
          </AppStack>
          <AppStack style={{ flex: 1, gap: 5, alignSelf: 'stretch' }}>
            <AppText
              style={{ color: 'dodgerblue', fontSize: 18, fontWeight: 500 }}
            >
              Overview
            </AppText>
            <Divider size={2} />

            {loading ? (
              <>
                <PaymentPriceItem isLoading label="Registration" />
                <PaymentPriceItem isLoading label="Transaction Total" />
                <PaymentPriceItem isLoading label="Total Payments" />
                <PaymentPriceItem isLoading label="Balance" />
              </>
            ) : (
              <>
                <PaymentPriceItem
                  currency={currency}
                  label={`Registration (${regFormSubmissionData.length})`}
                  value={
                    regFormSubmissionData.reduce(
                      (r, c) => r + c.lineItem.subtotalInCents,
                      0
                    ) / 100
                  }
                />

                {ecomOrderProductData && ecomOrderProductData.length > 0 && (
                  <PaymentPriceItem
                    currency={currency}
                    label={`Shop products (${ecomOrderProductData.length})`}
                    value={
                      ecomOrderProductData.reduce(
                        (r, c) => r + c.totalCost - c.taxTotal,
                        0
                      ) / 100
                    }
                  />
                )}

                {checkoutBalance.discountTotalInCents > 0 && (
                  <PaymentPriceItem
                    currency={currency}
                    label="Discount"
                    value={checkoutBalance.discountTotalInCents / 100}
                  />
                )}

                {checkoutBalance.taxTotalInCents > 0 && (
                  <PaymentPriceItem
                    currency={currency}
                    label="Tax"
                    value={checkoutBalance.taxTotalInCents / 100}
                  />
                )}

                {checkoutBalance.shippingTotalInCents > 0 && (
                  <PaymentPriceItem
                    currency={currency}
                    label="Shipping"
                    value={checkoutBalance.shippingTotalInCents / 100}
                  />
                )}

                {checkoutBalance.customerServiceFeeTotalInCents > 0 && (
                  <PaymentPriceItem
                    currency={currency}
                    label="Service fee"
                    value={checkoutBalance.customerServiceFeeTotalInCents / 100}
                  />
                )}

                {checkoutBalance.tieredPricingFeeInCents > 0 && (
                  <PaymentPriceItem
                    currency={currency}
                    label="Merch platform fee"
                    value={checkoutBalance.tieredPricingFeeInCents / 100}
                  />
                )}

                {invoiceTotals.totalInCents > 0 && (
                  <PaymentPriceItem
                    currency={currency}
                    label="Invoice total"
                    value={invoiceTotals.totalInCents / 100}
                  />
                )}

                <PaymentPriceItem
                  currency={currency}
                  label="Transaction total"
                  value={
                    (checkoutBalance.totalInCents +
                      invoiceTotals.totalInCents -
                      invoiceTotals.refundedAmountInCents) /
                    100
                  }
                  weight={700}
                />

                <PaymentPriceItem
                  color="#067D62"
                  currency={currency}
                  label="Total payments"
                  value={
                    (checkoutBalance.amountPaidInCents +
                      invoiceTotals.amountPaidInCents) /
                    100
                  }
                />

                {checkoutBalance.totalForgivenessAmount > 0 && (
                  <RecordPaymentPriceItem
                    color="#067D62"
                    currency={currency}
                    label="Forgiveness"
                    value={checkoutBalance.totalForgivenessAmount / 100}
                  />
                )}

                {invoiceTotals.refundedAmountInCents > 0 && (
                  <PaymentPriceItem
                    color="#c40000"
                    currency={currency}
                    label="Invoice Refunds"
                    value={invoiceTotals.refundedAmountInCents / 100}
                  />
                )}

                {checkoutBalance.refundedAmountInCents > 0 && (
                  <PaymentPriceItem
                    color="#c40000"
                    currency={currency}
                    label="Order Refunds"
                    value={checkoutBalance.refundedAmountInCents / 100}
                  />
                )}

                <PaymentPriceItem
                  currency={currency}
                  isDividerHidden
                  label="Balance"
                  value={
                    (checkoutBalance.balanceInCents +
                      invoiceTotals.balanceInCents) /
                    100
                  }
                  weight={700}
                />
              </>
            )}
          </AppStack>
        </AppFlexbox>
        <AppStack>
          <AppStack style={{ gap: 5 }}>
            <AppText
              style={{ color: 'dodgerblue', fontSize: 18, fontWeight: 500 }}
            >
              Checkout summary
            </AppText>
            <Divider />
            <Table horizontalSpacing={0}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <AppStack style={{ fontSize: 14, fontWeight: 500 }}>
                      ITEMS
                    </AppStack>
                  </Table.Th>
                  <Table.Th>
                    <AppStack
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        textAlign: 'end'
                      }}
                    >
                      QTY
                    </AppStack>
                  </Table.Th>
                  <Table.Th>
                    <AppStack
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        textAlign: 'end'
                      }}
                    >
                      UNIT PRICE
                    </AppStack>
                  </Table.Th>
                  <Table.Th>
                    <AppStack
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        textAlign: 'end'
                      }}
                    >
                      AMOUNT
                    </AppStack>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading ? (
                  <>
                    <Table.Tr>
                      <Table.Td>
                        <Skeleton height={20} width="40%" />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton
                          height={20}
                          style={{ marginLeft: 'auto' }}
                          width={30}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton
                          height={20}
                          style={{ marginLeft: 'auto' }}
                          width={60}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton
                          height={20}
                          style={{ marginLeft: 'auto' }}
                          width={60}
                        />
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td>
                        <Skeleton height={20} width={100} />
                      </Table.Td>
                      <Table.Td />
                      <Table.Td />
                      <Table.Td>
                        <Skeleton
                          height={20}
                          style={{ marginLeft: 'auto' }}
                          width={60}
                        />
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td />
                      <Table.Td />
                      <Table.Td>
                        <Skeleton
                          height={20}
                          style={{ marginLeft: 'auto' }}
                          width={60}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Skeleton
                          height={20}
                          style={{ marginLeft: 'auto' }}
                          width={60}
                        />
                      </Table.Td>
                    </Table.Tr>
                  </>
                ) : (
                  <>
                    {checkoutBalance.lineItems
                      .sort(
                        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                      )
                      .map((lineItem) => (
                        <Table.Tr key={lineItem.entityId}>
                          <Table.Td>
                            <AppStack
                              style={{
                                fontSize: 14,
                                fontWeight: 650
                              }}
                            >
                              {lineItem.productName}
                            </AppStack>
                          </Table.Td>
                          <Table.Td>
                            <AppStack
                              style={{
                                fontSize: 14,
                                textAlign: 'end'
                              }}
                            >
                              {lineItem.quantity}
                            </AppStack>
                          </Table.Td>
                          <Table.Td>
                            <AppText style={{ textAlign: 'end' }}>
                              <NumberFormatter
                                decimalScale={2}
                                fixedDecimalScale
                                prefix="$"
                                style={{ fontSize: 14 }}
                                suffix=" CAD"
                                thousandSeparator
                                value={lineItem.totalInCents / 100}
                              />
                            </AppText>
                          </Table.Td>
                          <Table.Td>
                            <AppText style={{ textAlign: 'end' }}>
                              <NumberFormatter
                                decimalScale={2}
                                fixedDecimalScale
                                prefix="$"
                                style={{ fontSize: 14 }}
                                suffix=" CAD"
                                thousandSeparator
                                value={
                                  (lineItem.totalInCents * lineItem.quantity) /
                                  100
                                }
                              />
                            </AppText>
                          </Table.Td>
                        </Table.Tr>
                      ))}

                    {checkoutBalance.tieredPricingFeeInCents > 0 && (
                      <Table.Tr>
                        <Table.Td>
                          <AppStack
                            style={{
                              fontSize: 14,
                              fontWeight: 650
                            }}
                          >
                            Platform Fee
                          </AppStack>
                        </Table.Td>
                        <Table.Td />
                        <Table.Td />
                        <Table.Td>
                          <AppText style={{ textAlign: 'end' }}>
                            <NumberFormatter
                              decimalScale={2}
                              fixedDecimalScale
                              prefix="$"
                              style={{ fontSize: 14 }}
                              suffix=" CAD"
                              thousandSeparator
                              value={
                                checkoutBalance.tieredPricingFeeInCents / 100
                              }
                            />
                          </AppText>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    <Table.Tr>
                      <Table.Td />
                      <Table.Td />
                      <Table.Td>
                        <AppStack
                          style={{
                            fontSize: 14,
                            fontWeight: 650,
                            textAlign: 'end'
                          }}
                        >
                          TOTAL
                        </AppStack>
                      </Table.Td>
                      <Table.Td>
                        <AppText style={{ textAlign: 'end' }}>
                          <NumberFormatter
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                            style={{
                              fontSize: 14,
                              fontWeight: 650
                            }}
                            suffix=" CAD"
                            thousandSeparator
                            value={checkoutBalance.totalInCents / 100}
                          />
                        </AppText>
                      </Table.Td>
                    </Table.Tr>
                  </>
                )}
              </Table.Tbody>
            </Table>
          </AppStack>

          <AppStack style={{ gap: 5 }}>
            <AppText
              style={{ color: 'dodgerblue', fontSize: 18, fontWeight: 500 }}
            >
              Transactions
            </AppText>
            <RegCartOrderTransactionPaymentTable
              loading={loading}
              regCartOrder={cartOrder}
            />
          </AppStack>
        </AppStack>
      </AppStack>
    </ResponsiveModal>
  );
};

RegCartOrderTransactionModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onShowRefund: PropTypes.func,
  pkRegCart: PropTypes.number,
  regCartOrder: PropTypes.object,
  translatedOrder: PropTypes.object
};

export default RegCartOrderTransactionModal;
